import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';

/**
 * Fix-pass (DEMO-RML-WEB-MEMBER-MENU-AUDIT, coordinator follow-up): `login()`'s own
 * success path used to call a private `deviceAuth()` (a legacy multi-device gate:
 * fetch the account's registered devices, escalate with a SweetAlert prompt past a
 * limit, then delete-and-re-register), which itself called a private `executeApis()`
 * (a journey/subscription pre-fetch, then the ONLY navigation into the app this
 * service ever performed). Both are removed, the same treatment
 * `sign-in.component.ts`'s own near-identical dead subtree got this same pass, and for
 * the same three reasons:
 * - `AuthService.authenticate()` (what `login()` below actually calls) is documented,
 *   by its own header comment, as ALWAYS discarding the social token/type it is given
 *   and always attempting `login({ email: '', password: '' })` instead ("the new API
 *   has no social-login equivalent" yet); that request can never succeed, so
 *   `login()`'s `next` callback below, and therefore `deviceAuth()`, was never actually
 *   reached by any of this method's three real call sites (`GoogleSignInComponent`,
 *   `SocialAuthComponent`, `SocialSignUpComponent`), confirmed by reading all three.
 * - `deviceAuth()` called a retired legacy device-management RPC with no equivalent on
 *   the new API (the same rail `app.component.ts`'s own `deviceChecker()` and
 *   `sign-in.component.ts`'s own dead escalation called).
 * - The new API's own device-cap enforcement is off by default in any case
 *   (`DEVICE_CAP_ENFORCEMENT_ENABLED`, `rml-platform/apps/api/src/routes/devices.ts`),
 *   so there is no live policy to gate on even if this were rewired.
 *
 * Because `executeApis()` was the only place this service ever navigated the browser
 * into the app on a successful login, `login()`'s own `next` callback now does that
 * navigation directly instead (mirroring `sign-in.component.ts`'s own real `signIn()`)
 * rather than leaving a genuinely successful future social login with nowhere to go.
 * The journey/subscription pre-fetch `executeApis()` used to do is NOT replaced: this
 * task's own round-1 pass already established that `sign-in.component.ts`'s equivalent
 * pre-fetch is unnecessary, the member/home route fetches whatever it needs once it
 * loads.
 */
@Injectable({ providedIn: 'root' })
export class SocialService {
    /**
     * Constructor
     */
    constructor(private _authService: AuthService, public _router: Router, private route: ActivatedRoute) { }

    public login(data): Promise<any> {
        return new Promise((resolve, reject) => {
            this._authService.authenticate({
            }, data.socialToken, data.socialType).subscribe({
                next: async (response) => {
                    try {
                        localStorage.setItem("card", JSON.stringify(response.data));
                        this._authService.accessToken = await response.data._token;
                        this._authService._authenticated = true;
                        this._authService.socialLogin = true;
                        const redirectURL = this.route.snapshot.queryParamMap.get('redirectURL');
                        this._router.navigate([redirectURL || '/member/home']);
                        resolve(response);
                    } catch (error) {
                        reject(error);
                    }
                },
                error: (err) => {
                    console.error('Login failed:', err);
                    reject(err);
                }
            });
        });
    }

    /**
     * TODO(demo): social sign-up is intentionally NOT wired to `POST /v1/auth/register`
     * (task brief item 5, social sign-up remains unadapted legacy, left disabled
     * rather than half-working). `AuthService.register()` now types its request body
     * against the real, locked contract (`RmlRegisterRequest`, email/password/first_
     * name/last_name/accept_terms), which a social-only flow has no `password`/
     * `accept_terms` to supply for; there is also no social-registration equivalent on
     * the new API at all (`AuthService.authenticate()`'s own doc comment, mapping doc
     * §3 "Auth" gaps). Fails closed with a clear rejection; both call sites
     * (`GoogleSignInComponent`/`SocialAuthComponent`) already catch this and surface a
     * "something went wrong" message rather than silently no-oping.
     */
    public register(data: { socialToken: string; socialType: string }): Promise<void> {
        return Promise.reject(new Error('Social sign-up is not available in this demo.'));
    }

}
